import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import StatusBadge from "@/components/app/StatusBadge";

export default function Normalization() {
  const [rules, setRules] = useState([]);
  const [testField, setTestField] = useState("manufacturer");
  const [testValue, setTestValue] = useState("Apple Inc.");
  const [testResult, setTestResult] = useState(null);
  const [newRule, setNewRule] = useState({ field: "manufacturer", source_value: "", normalized_value: "" });

  const load = () => { api.get("/normalization/rules").then(({ data }) => setRules(data.items || [])); };
  useEffect(load, []);

  const preview = async () => {
    const { data } = await api.post("/normalization/preview", { field: testField, value: testValue });
    setTestResult(data);
  };

  const create = async () => {
    if (!newRule.source_value || !newRule.normalized_value) return toast.error("Fill both fields");
    try {
      await api.post("/normalization/rules", newRule);
      toast.success("Rule created");
      setNewRule({ ...newRule, source_value: "", normalized_value: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 max-w-[1600px]">
      <div className="lg:col-span-2 space-y-4">
        <div>
          <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">Asset Intelligence</div>
          <h1 className="text-2xl font-heading font-semibold text-slate-900 tracking-tight">Model Normalization</h1>
          <p className="text-sm text-slate-500 mt-0.5">Unify manufacturer, brand and model spellings from every data source. Original values are always preserved.</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-widest">
              <tr>
                <th className="px-3 py-2 text-left">Field</th>
                <th className="px-3 py-2 text-left">Source value</th>
                <th className="px-3 py-2 text-left">Normalized to</th>
                <th className="px-3 py-2 text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {rules.map((r) => (
                <tr key={r.id} className="border-t border-slate-100">
                  <td className="px-3 py-2 capitalize">{r.field}</td>
                  <td className="px-3 py-2 font-mono text-xs">{r.source_value}</td>
                  <td className="px-3 py-2 font-medium">{r.normalized_value}</td>
                  <td className="px-3 py-2"><StatusBadge value={r.active ? "active" : "inactive"} tone={r.active ? "green" : "slate"} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="space-y-4">
        <div className="bg-white border border-slate-200 rounded-md p-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Test normalization</div>
          <div className="space-y-2">
            <Select value={testField} onValueChange={setTestField}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>{["manufacturer", "brand", "model", "category"].map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
            </Select>
            <Input value={testValue} onChange={(e) => setTestValue(e.target.value)} placeholder="Source value…" className="h-9" />
            <Button className="w-full h-9 bg-slate-900 text-white" onClick={preview}>Preview</Button>
          </div>
          {testResult && (
            <div className="mt-3 text-xs">
              <StatusBadge value={testResult.status?.replace(/_/g, " ")} />
              {testResult.normalized && <div className="mt-1 text-slate-700">→ <span className="font-medium">{testResult.normalized}</span></div>}
            </div>
          )}
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">New rule</div>
          <div className="space-y-2">
            <Select value={newRule.field} onValueChange={(v) => setNewRule({ ...newRule, field: v })}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>{["manufacturer", "brand", "model", "category"].map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
            </Select>
            <Input value={newRule.source_value} onChange={(e) => setNewRule({ ...newRule, source_value: e.target.value })} placeholder="Source value" className="h-9" />
            <Input value={newRule.normalized_value} onChange={(e) => setNewRule({ ...newRule, normalized_value: e.target.value })} placeholder="Normalized value" className="h-9" />
            <Button className="w-full h-9" variant="outline" onClick={create}>Add rule</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
